import React, { useState } from 'react';
import styled from 'styled-components';
import Title from 'components/Title';
import ArrowButton from 'components/ArrowButton';
import Line from 'components/Line';
import { useTranslation } from 'react-i18next';
import { device } from 'helpers/device';
import Loader from 'components/Loader';

const ERROR_COLOR = '#B42318';

const Wrapper = styled.div`
	width: 100%;
	margin: auto;
	padding: 32px 16px;
	max-width: 960px;
	background-color: ${(props) => props.backgroundColor};
	box-sizing: border-box;
	
	@media ${device.laptop} { 
		padding-top: 80px;
		padding-bottom: 80px;
		padding-left: 80px;
		padding-right: 80px;
		box-sizing: content-box;
	}

	div.section-title {
		margin-bottom: 40px;
	}

	div.section-input {
		display: grid;
		grid-template-columns: 1fr;
		grid-template-rows: auto;
		grid-column-gap: 64px;
		grid-row-gap: 28px;
		margin-bottom: 40px;
		@media ${device.laptop} { 
			grid-template-columns: 1fr 1fr;
		}

		div.input-wrapper {
			padding: 12px 0px;
			display: block;
		}

		div.input-wrapper.full-width {
			grid-column: 1 / -1;
		}

		div.input-wrapper.message {
			height: 100%;
			textarea {
				border: 1px solid ${p => p.theme.colors.ascent.primary};
				box-sizing: border-box;
				height: calc(100% - 48px);
				padding: 16px;
			}
			label {
				padding-bottom: 16px;
			}
		}

		input[type="date"], input, textarea {
			padding: 4px 0px 8px;
			width: 100%;
			border: unset;
			border-bottom: 1px solid ${p => p.theme.colors.ascent.primary};
			font-size: 16px;
			color: ${p => p.theme.colors.ascent.primary};
			background-color: ${(props) => props.backgroundColor};
			font-family: inherit;
		}

		input[type="checkbox"], input[type="radio"] {
			width: auto;
			margin-right: 8px;
		}

		div.input-wrapper.has-error input[type="date"],
		div.input-wrapper.has-error input:not([type="checkbox"]):not([type="radio"]),
		div.input-wrapper.has-error textarea {
			border-bottom-color: ${ERROR_COLOR};
		}

		div.input-wrapper.has-error > label,
		.checkbox-wrapper.has-error label,
		.radio-group.has-error > label {
			color: ${ERROR_COLOR};
		}

		.checkbox-wrapper.has-error,
		.radio-group.has-error {
			outline: 1px solid ${ERROR_COLOR};
			outline-offset: 8px;
			border-radius: 2px;
		}
	
		label {
			font-size: 14px;
			display: block;
			color: ${p => p.theme.colors.text.secondary};
			margin-bottom: 4px;
		}

		.checkbox-wrapper, .radio-wrapper {
			display: flex;
			align-items: center;
			flex-wrap: wrap;
			padding: 12px 0px;
			margin-bottom: 20px;
			
			label {
				margin-bottom: 0;
				cursor: pointer;
			}

			&.full-width {
				grid-column: 1 / -1;
			}
		}

		.radio-group {
			grid-column: 1 / -1;
			padding: 12px 0px;
		}

		.field-error {
			display: block;
			width: 100%;
			flex-basis: 100%;
			margin-top: 8px;
			font-size: 13px;
			color: ${ERROR_COLOR};
		}

		.subsection-title {
			font-size: 18px;
			font-weight: 600;
			color: ${p => p.theme.colors.text.primary};
			margin-top: 20px;
			margin-bottom: 16px;
			grid-column: 1 / -1;
		}

		.address-group-title {
			font-size: 16px;
			font-weight: 600;
			color: ${p => p.theme.colors.text.primary};
			margin-top: 12px;
			margin-bottom: 4px;
			grid-column: 1 / -1;
		}
	}

	.messageSent {
		text-align: center;
		padding-top: 130px;
		padding-bottom: 130px;
		padding-left: 80px;
		padding-right: 80px;
	}

	.info-text {
		color: ${p => p.theme.colors.text.secondary};
		font-size: 14px;
		margin-top: 4px;
		grid-column: 1 / -1;
	}
`;

const TenancyTermination = () => {
	const { t } = useTranslation();
	
	// Property information
	const [address, setAddress] = useState('');
	const [postalCode, setPostalCode] = useState('');
	const [city, setCity] = useState('');
	const [leaseNumber, setLeaseNumber] = useState('');

	// Tenant 1
	const [tenant1Name, setTenant1Name] = useState('');
	const [tenant1Email, setTenant1Email] = useState('');
	const [tenant1Phone, setTenant1Phone] = useState('');
	const [tenant1Cpr, setTenant1Cpr] = useState('');
	const [tenant1NewStreet, setTenant1NewStreet] = useState('');
	const [tenant1NewPostalCode, setTenant1NewPostalCode] = useState('');

	// Tenant 2
	const [hasTwoTenants, setHasTwoTenants] = useState(false);
	const [tenant2Name, setTenant2Name] = useState('');
	const [tenant2Email, setTenant2Email] = useState('');
	const [tenant2Phone, setTenant2Phone] = useState('');
	const [tenant2Cpr, setTenant2Cpr] = useState('');
	const [tenant2NewStreet, setTenant2NewStreet] = useState('');
	const [tenant2NewPostalCode, setTenant2NewPostalCode] = useState('');

	// Early re-leasing (Tenant 1 only)
	const [wantsEarlyReleasing, setWantsEarlyReleasing] = useState(false);
	const [earlyReleasingDate, setEarlyReleasingDate] = useState('');
	const [earlyReleasingNoEarlierGuaranteeConfirmed, setEarlyReleasingNoEarlierGuaranteeConfirmed] = useState(false);

	// Termination details
	const [effectiveDate, setEffectiveDate] = useState(() => new Date().toISOString().split('T')[0]);
	const [reason, setReason] = useState('');
	const [notes, setNotes] = useState('');
	const [registrationNumber, setRegistrationNumber] = useState('');
	const [accountNumber, setAccountNumber] = useState('');
	const [usePrepaidRent, setUsePrepaidRent] = useState(null);

	// Contact info for viewing
	const [viewingContactName, setViewingContactName] = useState('');
	const [viewingContactPhone, setViewingContactPhone] = useState('');
	const [viewingContactEmail, setViewingContactEmail] = useState('');

	// Form state
	const [sendingMessage, setSendingMessage] = useState(false);
	const [messageSent, setMessageSent] = useState(false);
	const [errors, setErrors] = useState({});

	const validateEmail = (email) => {
		const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return re.test(email);
	};

	const clearError = (field) => {
		setErrors((prev) => {
			if (!prev[field]) return prev;
			const next = { ...prev };
			delete next[field];
			return next;
		});
	};

	const setFieldValue = (setter, field) => (e) => {
		setter(e.target.value);
		clearError(field);
	};

	const scrollToFirstError = (nextErrors) => {
		const firstField = Object.keys(nextErrors)[0];
		if (!firstField) return;

		requestAnimationFrame(() => {
			const el =
				document.getElementById(firstField) ||
				document.querySelector(`[data-error-field="${firstField}"]`);
			if (!el) return;
			el.scrollIntoView({ behavior: 'smooth', block: 'center' });
			if (typeof el.focus === 'function' && el.tagName !== 'DIV') {
				el.focus({ preventScroll: true });
			}
		});
	};

	const validateForm = () => {
		const nextErrors = {};
		const requiredMsg = t('opsigelse.validation.fieldRequired');

		const requireValue = (field, value, message = requiredMsg) => {
			if (value === null || value === undefined || value === false) {
				nextErrors[field] = message;
				return;
			}
			if (typeof value === 'string' && !value.trim()) {
				nextErrors[field] = message;
			}
		};

		requireValue('address', address);
		requireValue('postalCode', postalCode);
		requireValue('city', city);

		requireValue('tenant1Name', tenant1Name);
		requireValue('tenant1Email', tenant1Email);
		requireValue('tenant1Phone', tenant1Phone);
		requireValue('tenant1Cpr', tenant1Cpr);
		requireValue('tenant1NewStreet', tenant1NewStreet);
		requireValue('tenant1NewPostalCode', tenant1NewPostalCode);

		if (tenant1Email.trim() && !validateEmail(tenant1Email)) {
			nextErrors.tenant1Email = t('opsigelse.validation.emailInvalid');
		}

		if (wantsEarlyReleasing) {
			requireValue('earlyReleasingDate', earlyReleasingDate, t('opsigelse.validation.earlyReleasingDateRequired'));
			if (!earlyReleasingNoEarlierGuaranteeConfirmed) {
				nextErrors.earlyReleasingNoEarlierGuaranteeConfirmed = t('opsigelse.validation.earlyReleasingConfirmationRequired');
			}
		}

		if (hasTwoTenants) {
			requireValue('tenant2Name', tenant2Name);
			requireValue('tenant2Email', tenant2Email);
			requireValue('tenant2Phone', tenant2Phone);
			requireValue('tenant2Cpr', tenant2Cpr);
			requireValue('tenant2NewStreet', tenant2NewStreet);
			requireValue('tenant2NewPostalCode', tenant2NewPostalCode);

			if (tenant2Email.trim() && !validateEmail(tenant2Email)) {
				nextErrors.tenant2Email = t('opsigelse.validation.emailInvalid');
			}
		}

		requireValue('effectiveDate', effectiveDate, t('opsigelse.validation.effectiveDateRequired'));

		if (effectiveDate) {
			const selectedDate = new Date(effectiveDate);
			const today = new Date();
			today.setHours(0, 0, 0, 0);

			if (selectedDate < today) {
				nextErrors.effectiveDate = t('opsigelse.validation.dateMustBeFuture');
			}
		}

		if (usePrepaidRent === null) {
			nextErrors.usePrepaidRent = t('opsigelse.validation.prepaidRentRequired');
		}

		requireValue('registrationNumber', registrationNumber, t('opsigelse.validation.registrationNumberRequired'));
		requireValue('accountNumber', accountNumber, t('opsigelse.validation.accountNumberRequired'));

		setErrors(nextErrors);

		if (Object.keys(nextErrors).length > 0) {
			scrollToFirstError(nextErrors);
			return false;
		}

		return true;
	};

	const clearForm = () => {
		setAddress('');
		setPostalCode('');
		setCity('');
		setLeaseNumber('');
		setTenant1Name('');
		setTenant1Email('');
		setTenant1Phone('');
		setTenant1Cpr('');
		setTenant1NewStreet('');
		setTenant1NewPostalCode('');
		setHasTwoTenants(false);
		setTenant2Name('');
		setTenant2Email('');
		setTenant2Phone('');
		setTenant2Cpr('');
		setTenant2NewStreet('');
		setTenant2NewPostalCode('');
		setWantsEarlyReleasing(false);
		setEarlyReleasingDate('');
		setEarlyReleasingNoEarlierGuaranteeConfirmed(false);
		setEffectiveDate(new Date().toISOString().split('T')[0]);
		setReason('');
		setNotes('');
		setRegistrationNumber('');
		setAccountNumber('');
		setUsePrepaidRent(null);
		setViewingContactName('');
		setViewingContactPhone('');
		setViewingContactEmail('');
		setErrors({});
	};

	async function handleSubmit() {
		if (!validateForm()) {
			setSendingMessage(false);
			return;
		}

		setSendingMessage(true);

		try {
			const tenant1Data = {
				name: tenant1Name,
				email: tenant1Email,
				phone: tenant1Phone,
				cpr: tenant1Cpr,
				newAddress: {
					street: tenant1NewStreet,
					postalCode: tenant1NewPostalCode
				},
				earlyReleasing: wantsEarlyReleasing ? {
					wantsEarlyReleasing: true,
					date: earlyReleasingDate,
					noEarlierReleasingConfirmed: earlyReleasingNoEarlierGuaranteeConfirmed
				} : null
			};

			const tenant2Data = hasTwoTenants ? {
				name: tenant2Name,
				email: tenant2Email,
				phone: tenant2Phone,
				cpr: tenant2Cpr,
				newAddress: {
					street: tenant2NewStreet,
					postalCode: tenant2NewPostalCode
				}
			} : null;

			const formData = {
				property: {
					address,
					postalCode,
					city,
					leaseNumber: leaseNumber || null
				},
				tenant1: tenant1Data,
				tenant2: tenant2Data,
				termination: {
					effectiveDate,
					reason: reason || null,
					notes: notes || null,
					registrationNumber,
					accountNumber,
					usePrepaidRent
				},
				viewingContact: {
					name: viewingContactName || null,
					phone: viewingContactPhone || null,
					email: viewingContactEmail || null
				},
				submittedAt: new Date().toISOString(),
				url: window.location.href
			};

			const response = await fetch(
				`${process.env.REACT_APP_CLOUD_FUNCTIONS_URL}/submitTenancyTermination`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json'
					},
					body: JSON.stringify(formData)
				}
			);

			if (!response.ok) {
				throw new Error(`Submit failed with status ${response.status}`);
			}

			const responseJson = await response.json();
			if (!responseJson || !responseJson.ok) {
				throw new Error('Submission was not accepted by backend');
			}

			clearForm();
			setSendingMessage(false);
			setMessageSent(true);
		} catch (e) {
			console.error('Error submitting termination: ', e);
			alert(t('opsigelse.errors.technical'));
			setSendingMessage(false);
		}
	}

	const fieldClassName = (field, extra = '') =>
		`input-wrapper${extra ? ` ${extra}` : ''}${errors[field] ? ' has-error' : ''}`;

	const formContent = (
		<>
			<div className="section-title">
				<Line />
				<Title>{t('opsigelse.title')}</Title>
				<p className="info-text">{t('opsigelse.intro')}</p>
			</div>

			<div className="section-input">
				<h3 className="subsection-title">{t('opsigelse.sections.property')}</h3>
				
				<div className={fieldClassName('address')}>
					<label htmlFor="address">{t('opsigelse.fields.address')} *</label>
					<input
						id="address"
						type="text"
						value={address}
						aria-invalid={Boolean(errors.address)}
						onChange={setFieldValue(setAddress, 'address')}
					/>
					{errors.address && <span className="field-error">{errors.address}</span>}
				</div>

				<div className={fieldClassName('postalCode')}>
					<label htmlFor="postalCode">{t('opsigelse.fields.postalCode')} *</label>
					<input
						id="postalCode"
						type="text"
						value={postalCode}
						aria-invalid={Boolean(errors.postalCode)}
						onChange={setFieldValue(setPostalCode, 'postalCode')}
					/>
					{errors.postalCode && <span className="field-error">{errors.postalCode}</span>}
				</div>

				<div className={fieldClassName('city')}>
					<label htmlFor="city">{t('opsigelse.fields.city')} *</label>
					<input
						id="city"
						type="text"
						value={city}
						aria-invalid={Boolean(errors.city)}
						onChange={setFieldValue(setCity, 'city')}
					/>
					{errors.city && <span className="field-error">{errors.city}</span>}
				</div>

				<div className="input-wrapper">
					<label htmlFor="leaseNumber">{t('opsigelse.fields.leaseNumber')}</label>
					<input
						id="leaseNumber"
						type="text"
						value={leaseNumber}
						onChange={(e) => setLeaseNumber(e.target.value)}
					/>
				</div>

				<h3 className="subsection-title">{t('opsigelse.sections.tenant1')}</h3>

				<div className="checkbox-wrapper full-width">
					<input
						id="hasTwoTenants"
						type="checkbox"
						checked={hasTwoTenants}
						onChange={(e) => {
							const checked = e.target.checked;
							setHasTwoTenants(checked);
							if (!checked) {
								setTenant2Name('');
								setTenant2Email('');
								setTenant2Phone('');
								setTenant2Cpr('');
								setTenant2NewStreet('');
								setTenant2NewPostalCode('');
								setErrors((prev) => {
									const next = { ...prev };
									delete next.tenant2Name;
									delete next.tenant2Email;
									delete next.tenant2Phone;
									delete next.tenant2Cpr;
									delete next.tenant2NewStreet;
									delete next.tenant2NewPostalCode;
									return next;
								});
							}
						}}
					/>
					<label htmlFor="hasTwoTenants">{t('opsigelse.fields.hasTwoTenants')}</label>
				</div>

				<div className={fieldClassName('tenant1Name')}>
					<label htmlFor="tenant1Name">{t('opsigelse.fields.name')} *</label>
					<input
						id="tenant1Name"
						type="text"
						value={tenant1Name}
						aria-invalid={Boolean(errors.tenant1Name)}
						onChange={setFieldValue(setTenant1Name, 'tenant1Name')}
					/>
					{errors.tenant1Name && <span className="field-error">{errors.tenant1Name}</span>}
				</div>

				<div className={fieldClassName('tenant1Email')}>
					<label htmlFor="tenant1Email">{t('opsigelse.fields.email')} *</label>
					<input
						id="tenant1Email"
						type="email"
						value={tenant1Email}
						aria-invalid={Boolean(errors.tenant1Email)}
						onChange={setFieldValue(setTenant1Email, 'tenant1Email')}
					/>
					{errors.tenant1Email && <span className="field-error">{errors.tenant1Email}</span>}
				</div>

				<div className={fieldClassName('tenant1Phone')}>
					<label htmlFor="tenant1Phone">{t('opsigelse.fields.phone')} *</label>
					<input
						id="tenant1Phone"
						type="tel"
						value={tenant1Phone}
						aria-invalid={Boolean(errors.tenant1Phone)}
						onChange={setFieldValue(setTenant1Phone, 'tenant1Phone')}
					/>
					{errors.tenant1Phone && <span className="field-error">{errors.tenant1Phone}</span>}
				</div>

				<div className={fieldClassName('tenant1Cpr')}>
					<label htmlFor="tenant1Cpr">{t('opsigelse.fields.cpr')} *</label>
					<input
						id="tenant1Cpr"
						type="text"
						value={tenant1Cpr}
						aria-invalid={Boolean(errors.tenant1Cpr)}
						onChange={setFieldValue(setTenant1Cpr, 'tenant1Cpr')}
					/>
					{errors.tenant1Cpr && <span className="field-error">{errors.tenant1Cpr}</span>}
				</div>

				<p className="address-group-title">{t('opsigelse.fields.newAddressGroupTitle')}</p>
				<p className="info-text">{t('opsigelse.fields.newAddressHint')}</p>

				<div className={fieldClassName('tenant1NewStreet')}>
					<label htmlFor="tenant1NewStreet">{t('opsigelse.fields.newAddressStreet')} *</label>
					<input
						id="tenant1NewStreet"
						type="text"
						value={tenant1NewStreet}
						placeholder={t('opsigelse.fields.newAddressStreetPlaceholder')}
						aria-invalid={Boolean(errors.tenant1NewStreet)}
						onChange={setFieldValue(setTenant1NewStreet, 'tenant1NewStreet')}
					/>
					{errors.tenant1NewStreet && <span className="field-error">{errors.tenant1NewStreet}</span>}
				</div>

				<div className={fieldClassName('tenant1NewPostalCode')}>
					<label htmlFor="tenant1NewPostalCode">{t('opsigelse.fields.newAddressPostalCode')} *</label>
					<input
						id="tenant1NewPostalCode"
						type="text"
						value={tenant1NewPostalCode}
						placeholder={t('opsigelse.fields.newAddressPostalCodePlaceholder')}
						aria-invalid={Boolean(errors.tenant1NewPostalCode)}
						onChange={setFieldValue(setTenant1NewPostalCode, 'tenant1NewPostalCode')}
					/>
					{errors.tenant1NewPostalCode && <span className="field-error">{errors.tenant1NewPostalCode}</span>}
				</div>

				<div className="checkbox-wrapper full-width">
					<input
						id="wantsEarlyReleasing"
						type="checkbox"
						checked={wantsEarlyReleasing}
						onChange={(e) => {
							const checked = e.target.checked;
							setWantsEarlyReleasing(checked);
							if (!checked) {
								setEarlyReleasingDate('');
								setEarlyReleasingNoEarlierGuaranteeConfirmed(false);
								setErrors((prev) => {
									const next = { ...prev };
									delete next.earlyReleasingDate;
									delete next.earlyReleasingNoEarlierGuaranteeConfirmed;
									return next;
								});
							}
						}}
					/>
					<label htmlFor="wantsEarlyReleasing">{t('opsigelse.fields.wantsEarlyReleasing')}</label>
				</div>

				{wantsEarlyReleasing && (
					<>
						<div className="input-wrapper full-width">
							<p className="info-text">{t('opsigelse.fields.earlyReleasingDisclaimer')}</p>
						</div>
						<div className={fieldClassName('earlyReleasingDate')}>
							<label htmlFor="earlyReleasingDate">{t('opsigelse.fields.earlyReleasingDate')} *</label>
							<input
								id="earlyReleasingDate"
								type="date"
								value={earlyReleasingDate}
								aria-invalid={Boolean(errors.earlyReleasingDate)}
								onChange={setFieldValue(setEarlyReleasingDate, 'earlyReleasingDate')}
							/>
							{errors.earlyReleasingDate && <span className="field-error">{errors.earlyReleasingDate}</span>}
						</div>
						<div className="input-wrapper full-width">
							<p className="info-text">{t('opsigelse.fields.earlyReleasingFraflytningInfo')}</p>
							<p className="info-text">{t('opsigelse.fields.earlyReleasingDateInfo')}</p>
						</div>

						<div
							className={`checkbox-wrapper full-width${errors.earlyReleasingNoEarlierGuaranteeConfirmed ? ' has-error' : ''}`}
							data-error-field="earlyReleasingNoEarlierGuaranteeConfirmed"
						>
							<input
								id="earlyReleasingNoEarlierGuaranteeConfirmed"
								type="checkbox"
								checked={earlyReleasingNoEarlierGuaranteeConfirmed}
								aria-invalid={Boolean(errors.earlyReleasingNoEarlierGuaranteeConfirmed)}
								onChange={(e) => {
									setEarlyReleasingNoEarlierGuaranteeConfirmed(e.target.checked);
									clearError('earlyReleasingNoEarlierGuaranteeConfirmed');
								}}
							/>
							<label htmlFor="earlyReleasingNoEarlierGuaranteeConfirmed">
								{t('opsigelse.fields.earlyReleasingNoEarlierGuaranteeConfirm')} *
							</label>
							{errors.earlyReleasingNoEarlierGuaranteeConfirmed && (
								<span className="field-error">{errors.earlyReleasingNoEarlierGuaranteeConfirmed}</span>
							)}
						</div>
					</>
				)}

				{hasTwoTenants && (
					<>
						<h3 className="subsection-title">{t('opsigelse.sections.tenant2')}</h3>

						<div className={fieldClassName('tenant2Name')}>
							<label htmlFor="tenant2Name">{t('opsigelse.fields.name')} *</label>
							<input
								id="tenant2Name"
								type="text"
								value={tenant2Name}
								aria-invalid={Boolean(errors.tenant2Name)}
								onChange={setFieldValue(setTenant2Name, 'tenant2Name')}
							/>
							{errors.tenant2Name && <span className="field-error">{errors.tenant2Name}</span>}
						</div>

						<div className={fieldClassName('tenant2Email')}>
							<label htmlFor="tenant2Email">{t('opsigelse.fields.email')} *</label>
							<input
								id="tenant2Email"
								type="email"
								value={tenant2Email}
								aria-invalid={Boolean(errors.tenant2Email)}
								onChange={setFieldValue(setTenant2Email, 'tenant2Email')}
							/>
							{errors.tenant2Email && <span className="field-error">{errors.tenant2Email}</span>}
						</div>

						<div className={fieldClassName('tenant2Phone')}>
							<label htmlFor="tenant2Phone">{t('opsigelse.fields.phone')} *</label>
							<input
								id="tenant2Phone"
								type="tel"
								value={tenant2Phone}
								aria-invalid={Boolean(errors.tenant2Phone)}
								onChange={setFieldValue(setTenant2Phone, 'tenant2Phone')}
							/>
							{errors.tenant2Phone && <span className="field-error">{errors.tenant2Phone}</span>}
						</div>

						<div className={fieldClassName('tenant2Cpr')}>
							<label htmlFor="tenant2Cpr">{t('opsigelse.fields.cpr')} *</label>
							<input
								id="tenant2Cpr"
								type="text"
								value={tenant2Cpr}
								aria-invalid={Boolean(errors.tenant2Cpr)}
								onChange={setFieldValue(setTenant2Cpr, 'tenant2Cpr')}
							/>
							{errors.tenant2Cpr && <span className="field-error">{errors.tenant2Cpr}</span>}
						</div>

						<p className="address-group-title">{t('opsigelse.fields.newAddressGroupTitleTenant2')}</p>
						<p className="info-text">{t('opsigelse.fields.newAddressHint')}</p>

						<div className={fieldClassName('tenant2NewStreet')}>
							<label htmlFor="tenant2NewStreet">{t('opsigelse.fields.newAddressStreet')} *</label>
							<input
								id="tenant2NewStreet"
								type="text"
								value={tenant2NewStreet}
								placeholder={t('opsigelse.fields.newAddressStreetPlaceholder')}
								aria-invalid={Boolean(errors.tenant2NewStreet)}
								onChange={setFieldValue(setTenant2NewStreet, 'tenant2NewStreet')}
							/>
							{errors.tenant2NewStreet && <span className="field-error">{errors.tenant2NewStreet}</span>}
						</div>

						<div className={fieldClassName('tenant2NewPostalCode')}>
							<label htmlFor="tenant2NewPostalCode">{t('opsigelse.fields.newAddressPostalCode')} *</label>
							<input
								id="tenant2NewPostalCode"
								type="text"
								value={tenant2NewPostalCode}
								placeholder={t('opsigelse.fields.newAddressPostalCodePlaceholder')}
								aria-invalid={Boolean(errors.tenant2NewPostalCode)}
								onChange={setFieldValue(setTenant2NewPostalCode, 'tenant2NewPostalCode')}
							/>
							{errors.tenant2NewPostalCode && <span className="field-error">{errors.tenant2NewPostalCode}</span>}
						</div>
					</>
				)}

				<h3 className="subsection-title">{t('opsigelse.sections.termination')}</h3>

				<div className={fieldClassName('effectiveDate')}>
					<label htmlFor="effectiveDate">{t('opsigelse.fields.effectiveDate')} *</label>
					<input
						id="effectiveDate"
						type="date"
						value={effectiveDate}
						min={new Date().toISOString().split('T')[0]}
						aria-invalid={Boolean(errors.effectiveDate)}
						onChange={setFieldValue(setEffectiveDate, 'effectiveDate')}
					/>
					{errors.effectiveDate && <span className="field-error">{errors.effectiveDate}</span>}
				</div>

				<div className="input-wrapper full-width">
					<p className="info-text">{t('opsigelse.fields.effectiveDateNotice')}</p>
				</div>

				<div
					className={`radio-group${errors.usePrepaidRent ? ' has-error' : ''}`}
					data-error-field="usePrepaidRent"
				>
					<label>{t('opsigelse.fields.wantsPrepaidRent')} *</label>
					<div className="radio-wrapper">
						<input
							id="usePrepaidRentYes"
							type="radio"
							name="usePrepaidRent"
							checked={usePrepaidRent === true}
							onChange={() => {
								setUsePrepaidRent(true);
								clearError('usePrepaidRent');
							}}
						/>
						<label htmlFor="usePrepaidRentYes">{t('opsigelse.fields.yes')}</label>
					</div>
					<div className="radio-wrapper">
						<input
							id="usePrepaidRentNo"
							type="radio"
							name="usePrepaidRent"
							checked={usePrepaidRent === false}
							onChange={() => {
								setUsePrepaidRent(false);
								clearError('usePrepaidRent');
							}}
						/>
						<label htmlFor="usePrepaidRentNo">{t('opsigelse.fields.no')}</label>
					</div>
					{errors.usePrepaidRent && <span className="field-error">{errors.usePrepaidRent}</span>}
				</div>

				<div className="input-wrapper full-width">
					<p className="info-text">{t('opsigelse.fields.registrationAccountInfo')}</p>
				</div>

				<div className={fieldClassName('registrationNumber')}>
					<label htmlFor="registrationNumber">{t('opsigelse.fields.registrationNumber')} *</label>
					<input
						id="registrationNumber"
						type="text"
						value={registrationNumber}
						aria-invalid={Boolean(errors.registrationNumber)}
						onChange={setFieldValue(setRegistrationNumber, 'registrationNumber')}
					/>
					{errors.registrationNumber && <span className="field-error">{errors.registrationNumber}</span>}
				</div>

				<div className={fieldClassName('accountNumber')}>
					<label htmlFor="accountNumber">{t('opsigelse.fields.accountNumber')} *</label>
					<input
						id="accountNumber"
						type="text"
						value={accountNumber}
						aria-invalid={Boolean(errors.accountNumber)}
						onChange={setFieldValue(setAccountNumber, 'accountNumber')}
					/>
					{errors.accountNumber && <span className="field-error">{errors.accountNumber}</span>}
				</div>

				<div className="input-wrapper message full-width">
					<label htmlFor="reason">{t('opsigelse.fields.reason')}</label>
					<textarea
						id="reason"
						rows={4}
						value={reason}
						onChange={(e) => setReason(e.target.value)}
					/>
				</div>

				<div className="input-wrapper message full-width">
					<label htmlFor="notes">{t('opsigelse.fields.notes')}</label>
					<textarea
						id="notes"
						rows={4}
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
					/>
				</div>

				<h3 className="subsection-title">{t('opsigelse.sections.viewingContact')}</h3>

				<div className="input-wrapper full-width">
					<p className="info-text">{t('opsigelse.fields.viewingContactInfo')}</p>
				</div>

				<div className="input-wrapper">
					<label htmlFor="viewingContactName">{t('opsigelse.fields.viewingContactName')}</label>
					<input
						id="viewingContactName"
						type="text"
						value={viewingContactName}
						onChange={(e) => setViewingContactName(e.target.value)}
					/>
				</div>

				<div className="input-wrapper">
					<label htmlFor="viewingContactPhone">{t('opsigelse.fields.viewingContactPhone')}</label>
					<input
						id="viewingContactPhone"
						type="tel"
						value={viewingContactPhone}
						onChange={(e) => setViewingContactPhone(e.target.value)}
					/>
				</div>

				<div className="input-wrapper">
					<label htmlFor="viewingContactEmail">{t('opsigelse.fields.viewingContactEmail')}</label>
					<input
						id="viewingContactEmail"
						type="email"
						value={viewingContactEmail}
						onChange={(e) => setViewingContactEmail(e.target.value)}
					/>
				</div>
			</div>

			<ArrowButton onClick={handleSubmit}>{t('opsigelse.buttonLabel')}</ArrowButton>
		</>
	);

	const sendingMessageContent = (
		<div className='sendingMessage'>
			<Loader/>
		</div>
	);

	const messageSentContent = (
		<div className='messageSent'>
			<p>{t('opsigelse.success.message')}</p>
		</div>
	);

	return (
		<Wrapper backgroundColor="#fff">
			{!messageSent && !sendingMessage ? formContent : 
				sendingMessage ? sendingMessageContent : messageSentContent
			}
		</Wrapper>
	);
};

export default TenancyTermination;
